import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { FlowTraceRecorder } from "@nexa/flowtrace-browser";
import { mountFlowTraceConsent } from "@nexa/flowtrace-consent";
import { useAppLocale } from "../i18n";
import { resolveDemoIngestEndpoint } from "../lib/demo-recording";
import { nexaLegalLinks } from "../lib/legal-links";
import {
  FLOWTRACE_PUBLIC_APP_ID,
  flowTraceSelfRecordingArea,
  toFlowTraceSelfRelease,
  toFlowTraceSelfRoute,
} from "../lib/public-recording";

let activeRecorder: FlowTraceRecorder | null = null;

/**
 * FlowTrace自身の利用の流れ(ログイン→トークン発行→接続)を測るための意味のあるイベント。
 * 同意が無い・記録範囲外のときは何もしない。
 */
export const trackFlowTraceSelf = (
  name: string,
  properties: Record<string, string | number | boolean> = {},
): void => {
  activeRecorder?.track(name, properties);
};

/**
 * FlowTrace自身をFlowTraceで記録する。公開の開示ページと、ログイン〜ダッシュボード〜MCPアクセスが対象。
 * ダッシュボードには顧客サイトの利用者のデータが映るため、画面の文字は常に伏せ、
 * リプレイ・セッション一覧・トークン表示などは各コンポーネントで data-flowtrace-block にしている。
 */
export const PublicFlowTraceAnalytics = () => {
  const { pathname } = useLocation();
  const { locale } = useAppLocale();
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const area = flowTraceSelfRecordingArea(pathname);

  useEffect(() => {
    if (!area) return;

    let disposed = false;
    let decisionVersion = 0;
    let recorder: FlowTraceRecorder | null = null;

    const stopRecorder = async () => {
      const current = recorder;
      recorder = null;
      if (activeRecorder === current) activeRecorder = null;
      if (current) await current.stop();
    };

    const consent = mountFlowTraceConsent({
      appId: FLOWTRACE_PUBLIC_APP_ID,
      locale: localeRef.current === "ja-JP" ? "ja" : "en",
      onDecision: async (decision, privacy) => {
        const version = ++decisionVersion;
        if (decision === "denied") {
          await stopRecorder();
          return;
        }
        if (disposed || recorder) return;
        const { startFlowTrace } = await import("@nexa/flowtrace-browser");
        if (disposed || version !== decisionVersion || recorder) return;
        recorder = startFlowTrace({
          appId: FLOWTRACE_PUBLIC_APP_ID,
          endpoint: resolveDemoIngestEndpoint(window.location.hostname),
          maskAllText: area === "app" ? true : privacy.maskAllText,
          release: toFlowTraceSelfRelease(import.meta.env.VITE_FLOWTRACE_RELEASE),
          route: toFlowTraceSelfRoute(pathnameRef.current),
          sanitizeHref: (href) => {
            try {
              const url = new URL(href);
              return `${url.origin}${toFlowTraceSelfRoute(url.pathname)}`;
            } catch {
              return toFlowTraceSelfRoute(href);
            }
          },
          flushIntervalMs: 4_000,
          requestTimeoutMs: 10_000,
        });
        activeRecorder = recorder;
      },
      privacyPolicyUrl: nexaLegalLinks(localeRef.current).privacy,
      projectName: "FlowTrace",
      visibleText: area === "app" ? "masked" : "included",
    });

    return () => {
      disposed = true;
      decisionVersion++;
      consent.destroy();
      void stopRecorder().catch(() => undefined);
    };
  }, [area]);

  useEffect(() => {
    if (area) activeRecorder?.setRoute(toFlowTraceSelfRoute(pathname));
  }, [area, pathname]);

  return null;
};
